import { useEffect, useRef, useState } from "react";
import { History } from "lucide-react";

const TTL_MS = 7 * 24 * 60 * 60 * 1000;
const keyFor = (uid) => `wua.profileDraft.${uid}`;
const same = (a, b) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

function readDraft(uid) {
  try {
    const d = JSON.parse(localStorage.getItem(keyFor(uid)) || "null");
    if (d && d.saved_at && Date.now() - d.saved_at < TTL_MS && Object.keys(d.changes || {}).length) return d;
  } catch { /* corrupt draft: fall through and drop it */ }
  localStorage.removeItem(keyFor(uid));
  return null;
}

// Keeps unsaved profile edits on this device for 7 days; offers them back on the next visit.
export function useProfileDraft({ uid, ready, profile, changes, axes }) {
  const [draft, setDraft] = useState(null);
  const checked = useRef(false);

  useEffect(() => {
    if (!uid || !ready || checked.current) return;
    checked.current = true;
    const d = readDraft(uid);
    if (!d) return;
    const live = Object.fromEntries(Object.entries(d.changes).filter(([k, v]) => !same(v, profile[k])));
    if (Object.keys(live).length) setDraft({ ...d, changes: live });
    else localStorage.removeItem(keyFor(uid));
  }, [uid, ready, profile]);

  useEffect(() => {
    if (!uid || !checked.current) return;
    const merged = { ...(draft?.changes || {}), ...changes };
    if (!Object.keys(merged).length) { localStorage.removeItem(keyFor(uid)); return; }
    const mergedAxes = [...new Set([...(draft?.axes || []), ...axes])];
    localStorage.setItem(keyFor(uid), JSON.stringify({ saved_at: Date.now(), changes: merged, axes: mergedAxes }));
  }, [uid, draft, changes, axes]);

  const discard = () => setDraft(null);
  return { draft, discard };
}

function ago(ts) {
  const m = Math.round((Date.now() - ts) / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} hour${h > 1 ? "s" : ""} ago`;
  const d = Math.round(h / 24);
  return `${d} day${d > 1 ? "s" : ""} ago`;
}

export function DraftBanner({ draft, onRestore, onDiscard }) {
  if (!draft) return null;
  const n = Object.keys(draft.changes).length;
  return (
    <div data-testid="profile-draft-banner"
         className="mb-6 flex flex-wrap items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-[13px] text-amber-900">
      <History className="h-4 w-4 shrink-0" />
      <span className="flex-1 min-w-[12rem]" data-testid="profile-draft-text">
        You have {n} unsaved change{n > 1 ? "s" : ""} from {ago(draft.saved_at)} on this device.
      </span>
      <button type="button" onClick={onRestore} data-testid="profile-draft-restore"
              className="rounded-md bg-amber-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-amber-700">
        Restore
      </button>
      <button type="button" onClick={onDiscard} data-testid="profile-draft-discard"
              className="rounded-md border border-amber-300 px-3 py-1.5 text-xs font-medium text-amber-900 hover:bg-amber-100">
        Discard
      </button>
    </div>
  );
}
